import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { StatusBadge, PaymentBadge } from "../components/StatusBadge";
import { formatCurrency, formatDate, ORDER_STATUS_META } from "../lib/format";
import { Plus, X, PencilSimple, TrashSimple, Clock } from "@phosphor-icons/react";

const STATUS_KEYS = Object.keys(ORDER_STATUS_META);
const PAYMENT_KEYS = ["unpaid", "partial", "paid", "overdue"];

const empty = {
    client_name: "",
    mobile: "",
    service_id: "",
    description: "",
    assigned_employee: "",
    deadline: "",
    amount: 0,
    advance_paid: 0,
    payment_status: "unpaid",
    delivery_date: "",
    notes: "",
    status: "pending",
};

export default function Orders() {
    const [orders, setOrders] = useState([]);
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState(empty);
    const [filterStatus, setFilterStatus] = useState("");
    const [filterService, setFilterService] = useState("");
    const [q, setQ] = useState("");
    const [timelineOrder, setTimelineOrder] = useState(null);

    const load = async () => {
        setLoading(true);
        const params = {};
        if (filterStatus) params.status = filterStatus;
        if (filterService) params.service_id = filterService;
        if (q) params.q = q;
        const { data } = await api.get("/orders", { params });
        setOrders(data);
        setLoading(false);
    };

    useEffect(() => {
        api.get("/services")
            .then((r) => setServices(r.data))
            .catch(() => {});
    }, []);
    useEffect(() => {
        load();
        // eslint-disable-next-line
    }, [filterStatus, filterService, q]);

    const onOpen = (o) => {
        if (o) {
            setEditId(o.id);
            setForm({ ...empty, ...o });
        } else {
            setEditId(null);
            setForm(empty);
        }
        setShowModal(true);
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        const payload = {
            ...form,
            amount: Number(form.amount) || 0,
            advance_paid: Number(form.advance_paid) || 0,
        };
        if (editId) {
            await api.put(`/orders/${editId}`, payload);
        } else {
            await api.post("/orders", payload);
        }
        setShowModal(false);
        load();
    };

    const onDelete = async (id) => {
        if (!window.confirm("Delete this order?")) return;
        await api.delete(`/orders/${id}`);
        load();
    };

    return (
        <div className="space-y-6" data-testid="orders-page">
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <div className="label-uppercase">Operations</div>
                    <h1 className="font-display text-3xl font-bold tracking-tight mt-1">
                        Orders
                    </h1>
                    <p className="text-slate-400 text-sm mt-1">
                        Track every job from intake to delivery.
                    </p>
                </div>
                <button
                    data-testid="new-order-btn"
                    onClick={() => onOpen(null)}
                    className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 px-4 py-2.5 rounded-xl font-medium btn-glow"
                >
                    <Plus weight="bold" size={16} /> New Order
                </button>
            </div>

            <div className="glass-card p-4 flex flex-wrap gap-3 items-center">
                <input
                    data-testid="orders-search"
                    placeholder="Search by client / order no / mobile…"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm flex-1 min-w-[200px] focus:outline-none focus:border-blue-500/60"
                />
                <select
                    data-testid="filter-status"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500/60"
                >
                    <option value="">All statuses</option>
                    {STATUS_KEYS.map((k) => (
                        <option key={k} value={k}>
                            {ORDER_STATUS_META[k].label}
                        </option>
                    ))}
                </select>
                <select
                    data-testid="filter-service"
                    value={filterService}
                    onChange={(e) => setFilterService(e.target.value)}
                    className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500/60"
                >
                    <option value="">All services</option>
                    {services.map((s) => (
                        <option key={s.id} value={s.id}>
                            {s.name}
                        </option>
                    ))}
                </select>
            </div>

            <div className="glass-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="orders-table">
                        <thead>
                            <tr className="text-left text-slate-400 border-b border-white/5">
                                <th className="px-5 py-3 font-medium label-uppercase">Order</th>
                                <th className="px-5 py-3 font-medium label-uppercase">Client</th>
                                <th className="px-5 py-3 font-medium label-uppercase">Service</th>
                                <th className="px-5 py-3 font-medium label-uppercase">Deadline</th>
                                <th className="px-5 py-3 font-medium label-uppercase">Amount</th>
                                <th className="px-5 py-3 font-medium label-uppercase">Payment</th>
                                <th className="px-5 py-3 font-medium label-uppercase">Status</th>
                                <th className="px-5 py-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {orders.map((o) => (
                                <tr
                                    key={o.id}
                                    className="border-b border-white/5 hover:bg-white/[0.03] transition"
                                    data-testid={`order-row-${o.id}`}
                                >
                                    <td className="px-5 py-4 font-mono text-xs text-slate-300">
                                        {o.order_no}
                                    </td>
                                    <td className="px-5 py-4">
                                        <div className="font-medium">{o.client_name}</div>
                                        <div className="text-xs text-slate-500">{o.mobile}</div>
                                    </td>
                                    <td className="px-5 py-4 text-slate-300">
                                        {o.service_name || "—"}
                                    </td>
                                    <td className="px-5 py-4 text-slate-300">
                                        {formatDate(o.deadline)}
                                    </td>
                                    <td className="px-5 py-4">
                                        <div className="font-display font-semibold">
                                            {formatCurrency(o.amount)}
                                        </div>
                                        <div className="text-xs text-slate-500">
                                            Adv {formatCurrency(o.advance_paid)} · Due{" "}
                                            {formatCurrency(o.remaining_amount)}
                                        </div>
                                    </td>
                                    <td className="px-5 py-4">
                                        <PaymentBadge status={o.payment_status} />
                                    </td>
                                    <td className="px-5 py-4">
                                        <StatusBadge status={o.status} />
                                    </td>
                                    <td className="px-5 py-4">
                                        <div className="flex justify-end gap-2">
                                            <button
                                                onClick={() => setTimelineOrder(o)}
                                                className="p-2 rounded-lg hover:bg-white/5 text-slate-400 hover:text-cyan-300"
                                                title="Timeline"
                                                data-testid={`timeline-btn-${o.id}`}
                                            >
                                                <Clock size={16} />
                                            </button>
                                            <button
                                                onClick={() => onOpen(o)}
                                                className="p-2 rounded-lg hover:bg-white/5 text-slate-400 hover:text-blue-300"
                                                title="Edit"
                                                data-testid={`edit-order-${o.id}`}
                                            >
                                                <PencilSimple size={16} />
                                            </button>
                                            <button
                                                onClick={() => onDelete(o.id)}
                                                className="p-2 rounded-lg hover:bg-white/5 text-slate-400 hover:text-rose-400"
                                                title="Delete"
                                                data-testid={`delete-order-${o.id}`}
                                            >
                                                <TrashSimple size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {!loading && orders.length === 0 && (
                                <tr>
                                    <td
                                        colSpan={8}
                                        className="px-5 py-12 text-center text-slate-500"
                                    >
                                        No orders yet. Click "New Order" to add one.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {showModal && (
                <Modal title={editId ? "Edit Order" : "New Order"} onClose={() => setShowModal(false)}>
                    <form onSubmit={onSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Field label="Client Name" required>
                                <input
                                    data-testid="order-client-name"
                                    required
                                    value={form.client_name}
                                    onChange={(e) =>
                                        setForm({ ...form, client_name: e.target.value })
                                    }
                                    className="input"
                                />
                            </Field>
                            <Field label="Mobile" required>
                                <input
                                    data-testid="order-mobile"
                                    required
                                    value={form.mobile}
                                    onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                                    className="input"
                                />
                            </Field>
                            <Field label="Service" required>
                                <select
                                    data-testid="order-service"
                                    required
                                    value={form.service_id}
                                    onChange={(e) =>
                                        setForm({ ...form, service_id: e.target.value })
                                    }
                                    className="input"
                                >
                                    <option value="">Select service…</option>
                                    {services.map((s) => (
                                        <option key={s.id} value={s.id}>
                                            {s.name}
                                        </option>
                                    ))}
                                </select>
                            </Field>
                            <Field label="Assigned Employee">
                                <input
                                    data-testid="order-employee"
                                    value={form.assigned_employee || ""}
                                    onChange={(e) =>
                                        setForm({
                                            ...form,
                                            assigned_employee: e.target.value,
                                        })
                                    }
                                    className="input"
                                />
                            </Field>
                            <Field label="Deadline">
                                <input
                                    type="date"
                                    data-testid="order-deadline"
                                    value={form.deadline || ""}
                                    onChange={(e) => setForm({ ...form, deadline: e.target.value })}
                                    className="input"
                                />
                            </Field>
                            <Field label="Delivery Date">
                                <input
                                    type="date"
                                    data-testid="order-delivery"
                                    value={form.delivery_date || ""}
                                    onChange={(e) =>
                                        setForm({ ...form, delivery_date: e.target.value })
                                    }
                                    className="input"
                                />
                            </Field>
                            <Field label="Amount (₹)" required>
                                <input
                                    type="number"
                                    data-testid="order-amount"
                                    required
                                    value={form.amount}
                                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                                    className="input"
                                />
                            </Field>
                            <Field label="Advance Paid (₹)">
                                <input
                                    type="number"
                                    data-testid="order-advance"
                                    value={form.advance_paid}
                                    onChange={(e) =>
                                        setForm({ ...form, advance_paid: e.target.value })
                                    }
                                    className="input"
                                />
                            </Field>
                            <Field label="Status">
                                <select
                                    data-testid="order-status"
                                    value={form.status}
                                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                                    className="input"
                                >
                                    {STATUS_KEYS.map((k) => (
                                        <option key={k} value={k}>
                                            {ORDER_STATUS_META[k].label}
                                        </option>
                                    ))}
                                </select>
                            </Field>
                            <Field label="Payment Status">
                                <select
                                    data-testid="order-payment-status"
                                    value={form.payment_status}
                                    onChange={(e) =>
                                        setForm({ ...form, payment_status: e.target.value })
                                    }
                                    className="input"
                                >
                                    {PAYMENT_KEYS.map((k) => (
                                        <option key={k} value={k}>
                                            {k}
                                        </option>
                                    ))}
                                </select>
                            </Field>
                        </div>
                        <Field label="Description">
                            <textarea
                                data-testid="order-description"
                                rows={2}
                                value={form.description || ""}
                                onChange={(e) =>
                                    setForm({ ...form, description: e.target.value })
                                }
                                className="input"
                            />
                        </Field>
                        <Field label="Notes">
                            <textarea
                                data-testid="order-notes"
                                rows={2}
                                value={form.notes || ""}
                                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                className="input"
                            />
                        </Field>
                        <div className="flex justify-end gap-2 pt-2">
                            <button
                                type="button"
                                onClick={() => setShowModal(false)}
                                className="px-4 py-2 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-sm"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                data-testid="save-order"
                                className="px-5 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 btn-glow font-medium text-sm"
                            >
                                {editId ? "Save Changes" : "Create Order"}
                            </button>
                        </div>
                    </form>
                </Modal>
            )}

            {timelineOrder && (
                <Modal
                    title={`Timeline · ${timelineOrder.order_no}`}
                    onClose={() => setTimelineOrder(null)}
                >
                    <div className="space-y-3">
                        {(timelineOrder.timeline || []).map((ev, i) => (
                            <div key={i} className="flex items-start gap-3">
                                <div className="mt-1 h-2 w-2 rounded-full bg-blue-500" />
                                <div>
                                    <div className="text-sm font-medium">
                                        <StatusBadge status={ev.status} />{" "}
                                        <span className="text-slate-400 text-xs ml-2">
                                            {formatDate(ev.at)}
                                        </span>
                                    </div>
                                    {ev.note && (
                                        <div className="text-xs text-slate-500 mt-1">
                                            {ev.note}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </Modal>
            )}
        </div>
    );
}

export function Modal({ title, children, onClose }) {
    return (
        <div className="fixed inset-0 z-50 grid place-items-end sm:place-items-center bg-slate-950/70 backdrop-blur-sm p-0 sm:p-4">
            <div className="glass-card w-full sm:max-w-3xl max-h-[92vh] overflow-auto p-4 sm:p-6 animate-fade-up rounded-b-none sm:rounded-2xl">
                <div className="flex items-center justify-between mb-4">
                    <div className="font-display text-lg sm:text-xl font-semibold">{title}</div>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-lg hover:bg-white/5 text-slate-400"
                        aria-label="Close"
                    >
                        <X size={18} />
                    </button>
                </div>
                {children}
            </div>
        </div>
    );
}

export function Field({ label, required, children }) {
    return (
        <label className="block">
            <span className="label-uppercase">
                {label}
                {required && <span className="text-rose-400 ml-1">*</span>}
            </span>
            <div className="mt-1.5">{children}</div>
            <style>{`.input{width:100%;background:rgba(255,255,255,0.04);border:1px solid rgba(255,255,255,0.08);border-radius:0.75rem;padding:0.625rem 0.875rem;font-size:0.875rem;color:#f8fafc;outline:none;transition:.2s}.input:focus{border-color:rgba(59,130,246,0.5);box-shadow:0 0 0 1px rgba(59,130,246,0.35)}`}</style>
        </label>
    );
}
